import React, { createContext, useState, useContext } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';

const ProjectContext = createContext(null);

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const [currentProject, setCurrentProject] = useState(null);
  const [projects, setProjects] = useState([]);
  const [sensors, setSensors] = useState([]);
  const [labels, setLabels] = useState([]);
  const [manufacturers, setManufacturers] = useState([]);
  const [sensorModels, setSensorModels] = useState({});
  const [collaborators, setCollaborators] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await api.get('/projects/');
      // API returns paginated results
      const projectsData = response.data.results || response.data || [];
      setProjects(Array.isArray(projectsData) ? projectsData : []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      toast.error('Failed to fetch projects');
      setProjects([]); // Ensure projects is always an array
    } finally {
      setLoading(false);
    }
  };

  const fetchProject = async (projectId) => {
    try {
      setLoading(true);
      const response = await api.get(`/project/${projectId}/`);
      setCurrentProject(response.data);
      return response.data;
    } catch (error) {
      toast.error('Failed to fetch project');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const createProject = async (projectData) => {
    try {
      const response = await api.post('/projects/', projectData);
      toast.success('Project created successfully');
      await fetchProjects();
      return response.data;
    } catch (error) {
      toast.error('Failed to create project');
      throw error;
    }
  };

  const updateProject = async (projectId, projectData) => {
    try {
      const response = await api.put(`/project/${projectId}/`, projectData);
      setCurrentProject(response.data);
      toast.success('Project updated successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to update project');
      throw error;
    }
  };

  const deleteProject = async (projectId) => {
    try {
      await api.delete(`/project/${projectId}/`);
      toast.success('Project deleted successfully');
      await fetchProjects();
    } catch (error) {
      toast.error('Failed to delete project');
      throw error;
    }
  };

  const fetchSensors = async (projectId) => {
    try {
      const response = await api.get(`/project/${projectId}/sensors`);
      const sensorsData = response.data.results || response.data || [];
      setSensors(Array.isArray(sensorsData) ? sensorsData : []);
      return sensorsData;
    } catch (error) {
      console.error('Error fetching sensors:', error);
      toast.error('Failed to fetch sensors');
      setSensors([]);
      throw error;
    }
  };

  const fetchLabels = async (projectId) => {
    try {
      const response = await api.get(`/project/${projectId}/labels`);
      const labelsData = response.data.results || response.data || [];
      setLabels(Array.isArray(labelsData) ? labelsData : []);
      return labelsData;
    } catch (error) {
      console.error('Error fetching labels:', error);
      toast.error('Failed to fetch labels');
      setLabels([]);
      throw error;
    }
  };

  // Manufacturer operations
  const fetchManufacturers = async () => {
    try {
      const response = await api.get('/sensor-manufacturers/');
      const manufacturersData = response.data.results || response.data || [];
      setManufacturers(Array.isArray(manufacturersData) ? manufacturersData : []);
      return manufacturersData;
    } catch (error) {
      console.error('Error fetching manufacturers:', error);
      toast.error('Failed to fetch manufacturers');
      setManufacturers([]);
      throw error;
    }
  };

  const fetchSensorModels = async (manufacturerId) => {
    try {
      const response = await api.get(`/sensor-manufacturers/${manufacturerId}/models`);
      const modelsData = response.data.results || response.data || [];
      setSensorModels((prev) => ({
        ...prev,
        [manufacturerId]: Array.isArray(modelsData) ? modelsData : [],
      }));
      return modelsData;
    } catch (error) {
      console.error('Error fetching sensor models:', error);
      throw error;
    }
  };

  // Sensor CRUD operations
  const createSensor = async (projectId, sensorData) => {
    try {
      const response = await api.post(`/project/${projectId}/sensors/`, sensorData);
      toast.success('Sensor created successfully');
      await fetchSensors(projectId);
      return response.data;
    } catch (error) {
      toast.error('Failed to create sensor');
      throw error;
    }
  };

  const updateSensor = async (sensorId, sensorData) => {
    try {
      const response = await api.patch(`/sensor/${sensorId}/`, sensorData);
      toast.success('Sensor updated successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to update sensor');
      throw error;
    }
  };

  const deleteSensor = async (sensorId, projectId) => {
    try {
      await api.delete(`/sensor/${sensorId}/`);
      toast.success('Sensor deleted successfully');
      if (projectId) {
        await fetchSensors(projectId);
      }
    } catch (error) {
      toast.error('Failed to delete sensor');
      throw error;
    }
  };

  // Label CRUD operations
  const createLabel = async (projectId, labelData) => {
    try {
      const response = await api.post(`/project/${projectId}/labels/`, labelData);
      toast.success('Label created successfully');
      await fetchLabels(projectId);
      return response.data;
    } catch (error) {
      toast.error('Failed to create label');
      throw error;
    }
  };

  const updateLabel = async (labelId, labelData) => {
    try {
      const response = await api.put(`/label/${labelId}/`, labelData);
      toast.success('Label updated successfully');
      return response.data;
    } catch (error) {
      toast.error('Failed to update label');
      throw error;
    }
  };

  const deleteLabel = async (labelId, projectId) => {
    try {
      await api.delete(`/label/${labelId}/`);
      toast.success('Label deleted successfully');
      if (projectId) {
        await fetchLabels(projectId);
      }
    } catch (error) {
      toast.error('Failed to delete label');
      throw error;
    }
  };

  const bulkImportLabels = async (projectId, file) => {
    try {
      const formData = new FormData();
      formData.append('csv_file', file);
      const response = await api.post(`/project/${projectId}/labels/bulk/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      toast.success('Labels imported successfully');
      await fetchLabels(projectId);
      return response.data;
    } catch (error) {
      toast.error('Failed to import labels');
      throw error;
    }
  };

  // Collaborator operations
  const fetchCollaborators = async (projectId) => {
    try {
      const response = await api.get(`/project/${projectId}/collaborators/`);
      const data = response.data.results || response.data || [];
      setCollaborators(Array.isArray(data) ? data : []);
      return data;
    } catch (error) {
      console.error('Error fetching collaborators:', error);
      setCollaborators([]);
    }
  };

  const addCollaborator = async (projectId, email) => {
    try {
      const response = await api.post(`/project/${projectId}/collaborators/`, { email });
      toast.success(response.data?.message || 'Collaborator added');
      await fetchCollaborators(projectId);
      return response.data;
    } catch (error) {
      const msg = error.response?.data?.message || 'Failed to add collaborator';
      toast.error(msg);
      throw error;
    }
  };

  const removeCollaborator = async (projectId, userId) => {
    try {
      await api.delete(`/project/${projectId}/collaborators/${userId}/`);
      toast.success('Collaborator removed');
      await fetchCollaborators(projectId);
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to remove collaborator';
      toast.error(msg);
      throw error;
    }
  };

  const value = {
    currentProject,
    setCurrentProject,
    projects,
    setProjects,
    sensors,
    setSensors,
    labels,
    setLabels,
    manufacturers,
    setManufacturers,
    sensorModels,
    setSensorModels,
    loading,
    fetchProjects,
    fetchProject,
    createProject,
    updateProject,
    deleteProject,
    fetchSensors,
    fetchLabels,
    fetchManufacturers,
    fetchSensorModels,
    createSensor,
    updateSensor,
    deleteSensor,
    createLabel,
    updateLabel,
    deleteLabel,
    bulkImportLabels,
    collaborators,
    fetchCollaborators,
    addCollaborator,
    removeCollaborator,
  };

  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
};
